import { AlertCircle, Coins, Hourglass, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'

import { BuyCreditsButton } from '@/components/billing/BuyCreditsButton'
import { ToolCallCard } from '@/components/tools/ToolCallCard'
// Plug-in: renders [[photo:slug]] markers from the personal layer. An identity
// transform for any answer without one. See src/app/companion/README.md.
import { CompanionAnswer } from '@/features/companion/CompanionAnswer'
import { cn } from '@/lib/utils/cn'
import { formatMs } from '@/lib/utils/format'
import { toolTimeMs } from '@/features/chat/turnReducer'
import type { ChatTurn } from '@/types/events'

/**
 * The turn is paused on the model provider, with a live countdown.
 *
 * Without it a rate-limited turn sits silent for up to a minute and looks frozen — which is
 * exactly when someone gives up and sends the question again, spending more of the same
 * allowance.
 */
function WaitingNotice({ waiting }: { waiting: NonNullable<ChatTurn['waiting']> }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const left = Math.max(0, Math.ceil((waiting.until - now) / 1000))
  const when = left > 0 ? `in ${left}s` : 'now'
  const text =
    waiting.reason === 'rate_limit'
      ? `The model's rate limit was reached, so this turn is pausing. Continuing ${when} — the work so far is kept.`
      : `The model service had a temporary problem. Trying again ${when}.`

  return (
    <p
      role="status"
      className="mt-2 flex items-start gap-2 rounded-lg border border-line bg-raised px-3 py-2
        text-sm text-ink-muted"
    >
      <Hourglass aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-waiting" />
      <span>{text}</span>
    </p>
  )
}

/**
 * One question and everything that happened because of it.
 *
 * The tool activity sits between the question and the answer, in the order it occurred,
 * because that ordering is the explanation: the model chose a tool, the tool ran, then the
 * answer became possible. Hiding it behind a disclosure would make the system opaque again,
 * which is the thing this interface exists to prevent.
 */
export function ChatTurnView({
  turn,
}: {
  turn: ChatTurn
}) {
  const scaleMs = Math.max(...turn.activities.map((activity) => activity.latencyMs ?? 0), 1)
  const totalToolMs = toolTimeMs(turn)

  return (
    <article className="px-4 py-4 sm:px-6">
      {/* The person's message on the right, the assistant's work on the left — the layout
          every assistant uses, so nobody has to learn which side is which. */}
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-bubble-out
          px-4 py-2.5 text-base text-ink shadow-card">
          {turn.question}
        </p>
      </div>

      <div className="mt-4 flex gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full
            border border-line bg-raised text-ink-dim"
        >
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">

      {turn.activities.length > 0 ? (
        <div className="space-y-1.5">
          {turn.activities.map((activity) => (
            <ToolCallCard key={activity.callId} activity={activity} scaleMs={scaleMs}>
            </ToolCallCard>
          ))}
        </div>
      ) : null}

      {turn.status === 'streaming' && turn.waiting ? <WaitingNotice waiting={turn.waiting} /> : null}

      {turn.status === 'streaming' && !turn.waiting && turn.activities.length === 0 && !turn.answer ? (
        <p className="pt-1 text-sm text-ink-muted">
          Choosing a tool<span className="animate-blink">…</span>
        </p>
      ) : null}

      {turn.answer ? (
        <div
          className={cn(
            'max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-md bg-bubble-in px-4 py-2.5',
            'text-base leading-relaxed text-ink shadow-card',
            turn.activities.length > 0 ? 'mt-3' : 'mt-0.5',
          )}
        >
          <CompanionAnswer text={turn.answer} />
          {turn.status === 'streaming' ? (
            <span className="ml-0.5 inline-block h-4 w-1.5 translate-y-0.5 animate-blink bg-primary" />
          ) : null}
        </div>
      ) : null}

      {turn.status === 'failed' ? (
        turn.errorType === 'INSUFFICIENT_CREDITS' ? (
          // A turn can run out part-way through: the first rounds were affordable and the
          // next one was not. Nothing failed, so none of this is drawn in the failure red —
          // and the answer above it, as far as it got, stays on screen.
          <div
            data-testid="turn-out-of-credits"
            className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg
              border border-waiting/25 bg-waiting-wash px-3 py-2.5 text-sm text-ink-dim"
          >
            <Coins aria-hidden="true" className="h-4 w-4 shrink-0 text-waiting" />
            <span className="min-w-0 flex-1">{turn.errorMessage ?? 'This turn ran out of credits.'}</span>
            <BuyCreditsButton />
          </div>
        ) : (
          <p className="mt-3 flex items-start gap-2 rounded-lg border border-fail/25 bg-fail-wash
            px-3 py-2.5 text-sm text-fail">
            <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{turn.errorMessage ?? 'This turn did not finish.'}</span>
          </p>
        )
      ) : null}

      {turn.status === 'complete' && turn.totalLatencyMs ? (
        <p className="mt-3 text-micro text-ink-faint">
          {formatMs(turn.totalLatencyMs)} total
          {turn.activities.length > 0
            ? ` · ${turn.activities.length} tool call${turn.activities.length === 1 ? '' : 's'} in ${formatMs(totalToolMs)}`
            : ''}
        </p>
      ) : null}
        </div>
      </div>
    </article>
  )
}
